import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { formatDateShortLocalized } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import type { SecretStatus } from './SecretInput';

import styles from './SecretInput.module.scss';

export interface SecretInputStatusProps {
    id: string;
    status: SecretStatus;
    changedAt: Date | null;
    /** This box's own clear is pending; Undo takes it back. */
    clearing: boolean;
    /** A clear made elsewhere (the site's) takes this secret on save; it is undone there. */
    pendingClear: boolean;
    disabled: boolean;
    onUndo: () => void;
    undoRef?: Ref<HTMLButtonElement>;
}

/** The line under a secret box: what is stored, or that a clear is pending. */
function SecretInputStatus({
    id,
    status,
    changedAt,
    clearing,
    pendingClear,
    disabled,
    onUndo,
    undoRef,
}: SecretInputStatusProps) {
    const { t, i18n } = useTranslation('atoms');

    function statusText(): string {
        if (status === 'notSet') return t('secretInput.notSet');
        if (status === 'varies') return t('secretInput.varies');
        if (status === 'inherited') return t('secretInput.inherited');
        if (changedAt === null) return t('secretInput.stored');
        const date = formatDateShortLocalized(changedAt, parseLocale(i18n.language));
        return t('secretInput.storedOn', { date });
    }

    if (clearing)
        return (
            <p id={id} className={styles.clearing}>
                <span>{t('secretInput.willClear')}</span>
                <Button ref={undoRef} variant="link" onClick={onUndo} disabled={disabled}>
                    {t('secretInput.undo')}
                </Button>
            </p>
        );
    if (pendingClear)
        return (
            <p id={id} className={styles.clearing}>
                {t('secretInput.willClear')}
            </p>
        );
    return (
        <p id={id} className={styles.status}>
            {statusText()}
        </p>
    );
}

export default SecretInputStatus;
export { SecretInputStatus };
